import { PlusOutlined } from '@ant-design/icons';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Flex,
  Radio, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { ComparatorUnits, CompareAsUnits } from 'automations/data/comapreNode';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type CompareNodeData, type CompareNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveConditionNode } from 'automations/utils/conditionNode';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import CompareCriteriaBuilder from './CompareCriteriaBuilder';
import styles from './CompareNodeMenu.module.scss';

type Comparator = CompareNodeData['additionalOptions']['compare']['comparator'];
type Criteria = CompareNodeData['additionalOptions']['compare']['criterias'][number];

const initialFormData: Criteria = {
  conditionKey: '',
  conditionValue: '',
  comparator: ComparatorUnits.Is,
  compareAs: CompareAsUnits.Text,
};

export default function CompareNodeMenu({
  node,
}: {
  node: NodeProps<CompareNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const compareData = node.data?.additionalOptions?.compare;
  const [isLoading, setIsLoading] = useState(false);
  const [comparator, setComparator] = useState<Comparator>(compareData?.comparator ?? 'or');
  const [criterias, setCriterias] = useState<Criteria[]>(compareData?.criterias ?? [initialFormData]);

  const parentId = useSelector(getParentIdSelector(node.id));

  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const handleUpdateCriteriaAtIndex = useCallback((index: number, criteria: Criteria) => {
    setCriterias((prev) => prev.map((c, i) => (i === index ? criteria : c)));
  }, [setCriterias]);

  const handleDeleteCriteriaAtIndex = useCallback((index: number) => {
    setCriterias((prev) => prev.filter((_, i) => i !== index));
  }, [setCriterias]);

  const handleAddCriteria = useCallback(() => {
    setCriterias((prev) => [...prev, initialFormData]);
  }, [setCriterias]);

  const handleSaveNode = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.CompareNodeData.parse({
        ...node.data,
        additionalOptions: {
          compare: {
            comparator,
            criterias,
          },
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));
      setMenuOpen(false);

      await dispatch(saveConditionNode(node.id, parentId, contentMode));
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [comparator, criterias, contentMode, dispatch, node.data, node.id, parentId, setMenuOpen]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: 'Condition - Compare',
      }}
      extraLarge={false}
    >
      <Card
        title="Set Condition"
        extra={(
          <Flex gap={16}>
            <Typography.Text type="secondary">
              Choose Condition
            </Typography.Text>
            <Radio.Group value={comparator} onChange={(e) => setComparator(e.target.value)}>
              <Radio value="or">
                Any Condition(s)
              </Radio>
              <Radio value="and">
                All Condition(s)
              </Radio>
            </Radio.Group>
          </Flex>
        )}
      >
        <Flex vertical gap={24} className={styles.criteriasContainer}>
          {criterias.map((criteria, index) => (
            <CompareCriteriaBuilder
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              currentIndex={index}
              criteria={criteria}
              onChange={(newCriteria) => handleUpdateCriteriaAtIndex(index, newCriteria)}
              onDelete={() => handleDeleteCriteriaAtIndex(index)}
              comparator={comparator}
            />
          ))}
        </Flex>
        <Button type="link" icon={<PlusOutlined />} onClick={handleAddCriteria} className={styles.addConditionBtn}>
          Add Condition
        </Button>
      </Card>

      <Flex justify="flex-end" gap={16} className={styles.footer}>
        <Button
          type="primary"
          onClick={handleSaveNode}
          loading={isLoading}
        >
          Set Condition
        </Button>
      </Flex>
    </NodeMenu>
  );
}
